import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Button, Spinner, Alert, Table, Badge, Tabs, Tab, Collapse } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import {
    startProductSuggestionAnalysis,
    getSuggestionJobStatus,
    getProductSuggestions,
    acceptProductSuggestion,
    rejectProductSuggestion,
    acceptAllProductSuggestions,
    getAiBudget,
} from '../services/api';
import CurrencyInput from './CurrencyInput';

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes — no client-side axios timeout exists, so this is the hard stop.

const formatMoney = (value) => (value || value === 0 ? Math.round(value).toLocaleString('es-CO') : '—');
const formatUsd = (value) => (typeof value === 'number' ? `$${value.toFixed(2)}` : '—');

/**
 * Compact budget bar (task 017 follow-up, 2026-09-01 incident): what this business has spent
 * on AI analysis this month vs. what it's allowed. Shown whenever the budget is known —
 * across every phase, not just idle, since a run in progress can still hit the mid-run stop.
 */
const BudgetBar = ({ budget }) => {
    const { t } = useTranslation();
    if (!budget || budget.budget_usd === null) return null;

    const pctUsed = budget.budget_usd > 0 ? Math.min(100, (budget.consumed_usd / budget.budget_usd) * 100) : 100;
    const exhausted = budget.remaining_usd <= 0;

    return (
        <div className="mb-3 p-2 border rounded small">
            <div className="d-flex justify-content-between align-items-center mb-1">
                <span>
                    {t('productsPage.aiSuggestions.budgetLabel', 'Presupuesto de IA este mes')}:{' '}
                    <strong>{formatUsd(budget.consumed_usd)} / {formatUsd(budget.budget_usd)}</strong>
                </span>
                <span className={exhausted ? 'text-danger fw-semibold' : 'text-muted'}>
                    {exhausted
                        ? t('productsPage.aiSuggestions.budgetExhausted', 'Agotado')
                        : t('productsPage.aiSuggestions.budgetRemaining', { amount: formatUsd(budget.remaining_usd), defaultValue: `${formatUsd(budget.remaining_usd)} disponible` })}
                </span>
            </div>
            <div className="progress" style={{ height: 6 }}>
                <div
                    className={`progress-bar ${exhausted ? 'bg-danger' : 'bg-primary'}`}
                    style={{ width: `${pctUsed}%` }}
                    role="progressbar"
                    aria-valuenow={pctUsed}
                    aria-valuemin={0}
                    aria-valuemax={100}
                />
            </div>
            <div className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                {t('productsPage.aiSuggestions.budgetDisclaimer', {
                    pct: budget.percentage,
                    defaultValue: 'Estimado a partir del {{pct}}% del valor de tu suscripción; puede variar por fluctuaciones del dólar y cambios en el costo de los modelos de IA.',
                })}
            </div>
        </div>
    );
};

/**
 * One suggestion row: name, recommended price (editable before accepting), an expandable
 * "why" panel (price_stats + source_variants + rationale — the "informacion de referencia"
 * requirement), and accept/reject actions.
 */
const SuggestionRow = ({ suggestion, onAccept, onReject, busy }) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const [price, setPrice] = useState(suggestion.recommended_price || '');

    // Para 'existing': aceptar solo enlaza las ventas, nunca cambia el precio del producto —
    // el `recommended_price` de la sugerencia sale de las ventas de origen (que pueden traer
    // errores de tipeo) y no es lo que quedará vigente. Se muestra el precio actual real del
    // producto como dato principal, y el de las ventas solo como referencia cuando difieren
    // bastante (task 017 follow-up, 2026-09-01: caso real "tamal cerdo" a $4 vs producto a $13.000).
    const matchedPrice = suggestion.matched_product_price != null ? Number(suggestion.matched_product_price) : null;
    const recommendedPrice = suggestion.recommended_price != null ? Number(suggestion.recommended_price) : null;
    const pricesDiffer =
        suggestion.match_type === 'existing' &&
        matchedPrice !== null &&
        recommendedPrice !== null &&
        matchedPrice > 0 &&
        Math.abs(recommendedPrice - matchedPrice) / matchedPrice > 0.2;

    return (
        <>
            <tr>
                <td style={{ minWidth: 220 }}>
                    <div className="fw-semibold">{suggestion.suggested_name}</div>
                    {suggestion.suggested_description && (
                        <div className="text-muted small">{suggestion.suggested_description}</div>
                    )}
                    {suggestion.match_type === 'existing' && suggestion.matched_product_name && (
                        <Badge bg="info" className="mt-1">
                            {t('productsPage.aiSuggestions.linksTo', { name: suggestion.matched_product_name, defaultValue: `Se enlazará a: ${suggestion.matched_product_name}` })}
                        </Badge>
                    )}
                </td>
                <td style={{ minWidth: 140 }}>
                    {suggestion.match_type === 'new' ? (
                        <CurrencyInput value={price} onChange={setPrice} disabled={busy} />
                    ) : (
                        <div>
                            <span>{formatMoney(matchedPrice)}</span>
                            {pricesDiffer && (
                                <div
                                    data-testid="price-mismatch-warning"
                                    className="text-warning small d-flex align-items-center gap-1 mt-1"
                                    style={{ fontSize: '0.75rem' }}
                                >
                                    <AlertTriangle size={12} />
                                    <span>
                                        {t('productsPage.aiSuggestions.priceMismatch', {
                                            price: formatMoney(recommendedPrice),
                                            defaultValue: `Las ventas analizadas sugieren {{price}} — no se modificará el precio del producto`,
                                        })}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                </td>
                <td>
                    <Button variant="link" size="sm" onClick={() => setExpanded((v) => !v)} className="text-decoration-none">
                        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}{' '}
                        {t('productsPage.aiSuggestions.why', 'Por qué')}
                    </Button>
                </td>
                <td className="text-end" style={{ minWidth: 180 }}>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-2"
                        disabled={busy}
                        onClick={() => onReject(suggestion.suggestion_id)}
                    >
                        {t('productsPage.aiSuggestions.reject', 'Rechazar')}
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        disabled={busy}
                        onClick={() => onAccept(suggestion.suggestion_id, price)}
                    >
                        {t('productsPage.aiSuggestions.accept', 'Aceptar')}
                    </Button>
                </td>
            </tr>
            <tr>
                <td colSpan={4} className="p-0 border-0">
                    <Collapse in={expanded}>
                        <div className="bg-light px-3 py-2 small">
                            <div className="mb-1">
                                <strong>{t('productsPage.aiSuggestions.rationale', 'Justificación')}:</strong> {suggestion.rationale || '—'}
                            </div>
                            {suggestion.price_stats && (
                                <div className="mb-1">
                                    <strong>{t('productsPage.aiSuggestions.priceRange', 'Rango de precios')}:</strong>{' '}
                                    {formatMoney(suggestion.price_stats.min_price)} – {formatMoney(suggestion.price_stats.max_price)}
                                    {' '}({t('productsPage.aiSuggestions.occurrences', { count: suggestion.price_stats.total_occurrences, defaultValue: '{{count}} ventas' })})
                                </div>
                            )}
                            {Array.isArray(suggestion.source_variants) && suggestion.source_variants.length > 0 && (
                                <div>
                                    <strong>{t('productsPage.aiSuggestions.sourceVariants', 'Variantes agrupadas')}:</strong>
                                    <ul className="mb-0">
                                        {suggestion.source_variants.map((v) => (
                                            <li key={v.name}>
                                                "{v.name}" — {v.occurrences}x, {formatMoney(v.min_price)}–{formatMoney(v.max_price)}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </Collapse>
                </td>
            </tr>
        </>
    );
};

const SuggestionsTable = ({ suggestions, onAccept, onReject, busy, emptyMessage, priceLabel }) => {
    const { t } = useTranslation();
    if (suggestions.length === 0) {
        return <p className="text-muted p-3 mb-0">{emptyMessage}</p>;
    }
    return (
        <div className="table-responsive">
            <Table hover size="sm" className="align-middle mb-0">
                <thead>
                    <tr>
                        <th>{t('productsPage.aiSuggestions.name', 'Producto')}</th>
                        <th>{priceLabel || t('productsPage.aiSuggestions.price', 'Precio recomendado')}</th>
                        <th></th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {suggestions.map((s) => (
                        <SuggestionRow key={s.suggestion_id} suggestion={s} onAccept={onAccept} onReject={onReject} busy={busy} />
                    ))}
                </tbody>
            </Table>
        </div>
    );
};

const ProductSuggestionsModal = ({ show, onHide, onAccepted }) => {
    const { t } = useTranslation();
    // idle: nothing running, offer to start | running: polling a job | results: showing suggestions | error
    const [phase, setPhase] = useState('loading');
    const [suggestions, setSuggestions] = useState([]);
    const [errorCode, setErrorCode] = useState(null);
    const [budget, setBudget] = useState(null);
    const [partialNotice, setPartialNotice] = useState(null);
    const [progress, setProgress] = useState(null); // { completed, total } — task 017 follow-up, 2026-09-01
    const [busy, setBusy] = useState(false);
    const pollRef = useRef(null);
    const pollStartRef = useRef(null);

    const stopPolling = useCallback(() => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    }, []);

    const loadSuggestions = useCallback(async () => {
        const { data } = await getProductSuggestions({ status: 'pending', pagination: false, limit: 200, source: 'sales_history' });
        setSuggestions(data.suggestions || []);
        setPhase('results');
        // The run that just finished may have spent budget — refresh so the bar is accurate.
        getAiBudget().then(({ data: b }) => setBudget(b)).catch(() => {});
    }, []);

    const pollJob = useCallback(async () => {
        try {
            const { data: job } = await getSuggestionJobStatus('sales_history');
            if (!job) { setPhase('idle'); return; }

            if (job.status === 'completed') {
                stopPolling();
                setProgress(null);
                // 'completed' can still carry an error_code (PARTIAL_BATCH_FAILURE,
                // AI_BUDGET_EXCEEDED_MIDRUN) — some suggestions came through, but not every
                // group was analyzed. Surfaced as an info banner in the results view.
                setPartialNotice(job.error_code || null);
                await loadSuggestions();
            } else if (job.status === 'failed') {
                stopPolling();
                setProgress(null);
                setErrorCode(job.error_code || 'UNKNOWN_ERROR');
                setPhase('error');
                // A failed run can still have spent real money (e.g. a truncated batch) —
                // refresh so the bar reflects it, not just successful runs.
                getAiBudget().then(({ data: b }) => setBudget(b)).catch(() => {});
            } else if (Date.now() - (pollStartRef.current || Date.now()) > POLL_TIMEOUT_MS) {
                stopPolling();
                setProgress(null);
                setErrorCode('POLL_TIMEOUT');
                setPhase('error');
            } else if (job.total_batches != null) {
                // queued/running → keep polling. total_batches is null until aggregation
                // finishes (or the job has nothing to analyze) — leave progress at whatever
                // it was rather than flashing back to "unknown" between polls.
                setProgress({ completed: job.completed_batches || 0, total: job.total_batches });
            }
        } catch (err) {
            stopPolling();
            setErrorCode(err.response?.data?.messageCode || 'UNKNOWN_ERROR');
            setPhase('error');
        }
    }, [loadSuggestions, stopPolling]);

    const startPolling = useCallback(() => {
        setPhase('running');
        setProgress(null); // unknown until the first poll reports total_batches
        pollStartRef.current = Date.now();
        stopPolling();
        pollRef.current = setInterval(pollJob, POLL_INTERVAL_MS);
        pollJob(); // don't wait a full interval for the first check
    }, [pollJob, stopPolling]);

    // On open: resume an in-flight job, jump straight to results if there are already
    // pending suggestions from a previous run, or offer to start a new analysis.
    useEffect(() => {
        if (!show) return undefined;
        setPhase('loading');
        setErrorCode(null);
        setPartialNotice(null);

        getAiBudget().then(({ data }) => setBudget(data)).catch(() => setBudget(null));

        (async () => {
            try {
                const { data: job } = await getSuggestionJobStatus('sales_history');
                if (job && (job.status === 'queued' || job.status === 'running')) {
                    startPolling();
                    return;
                }

                const { data } = await getProductSuggestions({ status: 'pending', pagination: false, limit: 200, source: 'sales_history' });
                if ((data.suggestions || []).length > 0) {
                    setSuggestions(data.suggestions);
                    setPhase('results');
                } else {
                    setPhase('idle');
                }
            } catch {
                setPhase('idle');
            }
        })();

        return () => stopPolling();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    const handleStart = async (reanalyze = false) => {
        setBusy(true);
        setPartialNotice(null);
        try {
            await startProductSuggestionAnalysis(reanalyze);
            startPolling();
        } catch (err) {
            // Controller failures carry `messageCode` (see productSuggestions.controller.js);
            // the gating middlewares ahead of it (checkAiAnalysisLimit, checkAiBudget, ...)
            // carry `code` instead — same 403 shape as the rest of planValidator.js.
            const code = err.response?.data?.messageCode || err.response?.data?.code;
            if (code === 'ANALYSIS_IN_PROGRESS') {
                startPolling(); // someone else already started one — just watch it
            } else if (code === 'NO_ITEMS_TO_ANALYZE' || code === 'AI_BUDGET_EXCEEDED') {
                setErrorCode(code);
                setPhase('error');
                if (code === 'AI_BUDGET_EXCEEDED') {
                    getAiBudget().then(({ data: b }) => setBudget(b)).catch(() => {});
                }
            } else {
                toast.error(t(`productsPage.aiSuggestions.errors.${code}`, t('productsPage.aiSuggestions.errors.GENERIC', 'No se pudo iniciar el análisis.')));
            }
        } finally {
            setBusy(false);
        }
    };

    const handleAccept = async (suggestionId) => {
        setBusy(true);
        const toastId = toast.loading(t('productsPage.aiSuggestions.accepting', 'Aceptando...'));
        try {
            await acceptProductSuggestion(suggestionId);
            setSuggestions((prev) => prev.filter((s) => s.suggestion_id !== suggestionId));
            toast.success(t('productsPage.aiSuggestions.accepted', 'Producto creado/enlazado.'), { id: toastId });
            onAccepted?.();
        } catch (err) {
            const code = err.response?.data?.messageCode;
            toast.error(t(`productsPage.aiSuggestions.errors.${code}`, t('productsPage.aiSuggestions.errors.GENERIC', 'No se pudo aceptar la sugerencia.')), { id: toastId });
        } finally {
            setBusy(false);
        }
    };

    const handleReject = async (suggestionId) => {
        setBusy(true);
        try {
            await rejectProductSuggestion(suggestionId);
            setSuggestions((prev) => prev.filter((s) => s.suggestion_id !== suggestionId));
        } catch {
            toast.error(t('productsPage.aiSuggestions.errors.GENERIC', 'No se pudo rechazar la sugerencia.'));
        } finally {
            setBusy(false);
        }
    };

    const handleAcceptAll = async () => {
        setBusy(true);
        const toastId = toast.loading(t('productsPage.aiSuggestions.acceptingAll', 'Aceptando todas...'));
        try {
            const { data } = await acceptAllProductSuggestions('sales_history');
            setSuggestions((prev) => prev.filter((s) => !data.accepted.some((a) => a.suggestion_id === s.suggestion_id)));
            toast.success(t('productsPage.aiSuggestions.acceptedAll', { count: data.accepted.length, defaultValue: `{{count}} productos creados/enlazados.` }), { id: toastId });
            onAccepted?.();
        } catch (err) {
            const code = err.response?.data?.messageCode;
            toast.error(t(`productsPage.aiSuggestions.errors.${code}`, t('productsPage.aiSuggestions.errors.GENERIC', 'No se pudo completar la operación.')), { id: toastId });
        } finally {
            setBusy(false);
        }
    };

    const existing = suggestions.filter((s) => s.match_type === 'existing');
    const newOnes = suggestions.filter((s) => s.match_type === 'new');

    return (
        <Modal show={show} onHide={onHide} size="xl" centered>
            <Modal.Header closeButton>
                <Modal.Title>{t('productsPage.aiSuggestions.title', 'Productos sugeridos por IA')}</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ minHeight: 200 }}>
                <BudgetBar budget={budget} />

                {phase === 'loading' && (
                    <div className="text-center py-5">
                        <Spinner animation="border" />
                    </div>
                )}

                {phase === 'idle' && (
                    <div className="text-center py-4">
                        <p>{t('productsPage.aiSuggestions.idleDescription', 'Analiza el historial de ventas para detectar productos que aún no están estandarizados en tu catálogo.')}</p>
                        {budget && budget.budget_usd !== null && budget.remaining_usd <= 0 ? (
                            <Alert variant="warning" className="text-start">
                                {t('productsPage.aiSuggestions.errors.AI_BUDGET_EXCEEDED', 'Este negocio alcanzó su presupuesto de IA de este mes.')}
                            </Alert>
                        ) : (
                            <>
                                <Button variant="primary" disabled={busy} onClick={() => handleStart(false)}>
                                    {busy ? <Spinner size="sm" animation="border" /> : t('productsPage.aiSuggestions.start', 'Analizar ventas')}
                                </Button>
                                <div className="mt-2">
                                    <Button variant="link" size="sm" disabled={busy} onClick={() => handleStart(true)}>
                                        {t('productsPage.aiSuggestions.reanalyze', 'Volver a analizar todo el histórico')}
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {phase === 'running' && (
                    <div className="text-center py-5">
                        <Spinner animation="border" className="mb-3" />
                        <p className="mb-2">{t('productsPage.aiSuggestions.running', 'Analizando ventas con IA... esto puede tardar unos minutos.')}</p>
                        {progress ? (
                            <div className="mx-auto" data-testid="analysis-progress-bar" style={{ maxWidth: 360 }}>
                                <div className="progress" style={{ height: 8 }}>
                                    <div
                                        className="progress-bar"
                                        style={{ width: `${Math.min(100, (progress.completed / progress.total) * 100)}%` }}
                                        role="progressbar"
                                        aria-valuenow={progress.completed}
                                        aria-valuemin={0}
                                        aria-valuemax={progress.total}
                                    />
                                </div>
                                <div className="text-muted small mt-1">
                                    {t('productsPage.aiSuggestions.progressLabel', {
                                        completed: progress.completed,
                                        total: progress.total,
                                        defaultValue: 'Lote {{completed}} de {{total}}',
                                    })}
                                </div>
                            </div>
                        ) : (
                            <div className="text-muted small" data-testid="analysis-progress-unknown">
                                {t('productsPage.aiSuggestions.progressUnknown', 'Preparando el análisis...')}
                            </div>
                        )}
                    </div>
                )}

                {phase === 'error' && (
                    <Alert variant={['NO_ITEMS_TO_ANALYZE', 'AI_BUDGET_EXCEEDED'].includes(errorCode) ? 'info' : 'danger'}>
                        {t(`productsPage.aiSuggestions.errors.${errorCode}`, t('productsPage.aiSuggestions.errors.GENERIC', 'Ocurrió un error durante el análisis.'))}
                        <div className="mt-2">
                            <Button variant="outline-secondary" size="sm" onClick={() => setPhase('idle')}>
                                {t('productsPage.aiSuggestions.tryAgain', 'Volver a intentar')}
                            </Button>
                        </div>
                    </Alert>
                )}

                {phase === 'results' && (
                    <>
                        {partialNotice && (
                            <Alert variant="info" className="py-2">
                                {t(`productsPage.aiSuggestions.errors.${partialNotice}`, t('productsPage.aiSuggestions.errors.GENERIC', 'El análisis no terminó de procesar todas las ventas.'))}
                            </Alert>
                        )}
                        {suggestions.length > 0 && (
                            <div className="d-flex justify-content-end mb-2">
                                <Button variant="success" size="sm" disabled={busy} onClick={handleAcceptAll}>
                                    {t('productsPage.aiSuggestions.acceptAll', 'Aceptar todas')}
                                </Button>
                            </div>
                        )}
                        <Tabs defaultActiveKey="new" className="mb-2">
                            <Tab eventKey="new" title={`${t('productsPage.aiSuggestions.newTab', 'Productos nuevos')} (${newOnes.length})`}>
                                <SuggestionsTable
                                    suggestions={newOnes}
                                    onAccept={handleAccept}
                                    onReject={handleReject}
                                    busy={busy}
                                    emptyMessage={t('productsPage.aiSuggestions.noNew', 'No hay productos nuevos sugeridos.')}
                                />
                            </Tab>
                            <Tab eventKey="existing" title={`${t('productsPage.aiSuggestions.existingTab', 'Ya existen en tu catálogo')} (${existing.length})`}>
                                <SuggestionsTable
                                    suggestions={existing}
                                    onAccept={handleAccept}
                                    onReject={handleReject}
                                    busy={busy}
                                    emptyMessage={t('productsPage.aiSuggestions.noExisting', 'No se encontraron coincidencias con el catálogo actual.')}
                                    priceLabel={t('productsPage.aiSuggestions.currentPrice', 'Precio actual del producto')}
                                />
                            </Tab>
                        </Tabs>
                        <div className="mt-3">
                            <Button variant="link" size="sm" className="p-0" disabled={busy} onClick={() => handleStart(false)}>
                                {t('productsPage.aiSuggestions.runAgain', 'Analizar ventas nuevas otra vez')}
                            </Button>
                        </div>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>{t('common.close', 'Cerrar')}</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ProductSuggestionsModal;
// Reutilizados por GenerateProductsModal.js (task 017 follow-up, 2026-09-01) — misma barra de
// presupuesto de IA y mismo formato de dinero, sin duplicar el componente.
export { BudgetBar, formatMoney, formatUsd };
